/**
 * Singleton Niimbot BLE client with silent-reconnect support.
 *
 * Why: niimbluelib's `NiimbotBluetoothClient.connect()` always calls
 * `navigator.bluetooth.requestDevice(...)` which pops the OS chooser. This
 * module subclasses it to add `connectToDevice(device)` which skips the
 * chooser when we already have a permitted device, and exposes a singleton
 * so multiple print actions in one session reuse the open connection.
 */
import {
  NiimbotBluetoothClient,
  type ConnectionInfo,
} from '@mmote/niimbluelib';

class ReconnectableNiimbotBleClient extends NiimbotBluetoothClient {
  /**
   * Connect to a previously-permitted BluetoothDevice WITHOUT triggering
   * the OS chooser. Mirrors the body of the parent's `connect()` minus the
   * `requestDevice` call. Uses `(this as any)` to assign private fields
   * (`gattServer`, `channel`, `info`) since they're not exposed.
   */
  async connectToDevice(device: BluetoothDevice): Promise<ConnectionInfo> {
    if (!device.gatt) {
      throw new Error('Device has no GATT');
    }
    await this.disconnect();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const self = this as any;

    const onDisconnect = () => {
      self.gattServer = undefined;
      self.channel = undefined;
      self.info = {};
      device.removeEventListener('gattserverdisconnected', onDisconnect);
    };
    device.addEventListener('gattserverdisconnected', onDisconnect);

    const gattServer = await device.gatt.connect();
    const channel = await this.findSuitableCharacteristic(gattServer);
    if (!channel) {
      gattServer.disconnect();
      throw new Error('Suitable device characteristic not found');
    }

    channel.addEventListener('characteristicvaluechanged', (event) => {
      const target = event.target as BluetoothRemoteGATTCharacteristic;
      if (target.value) {
        self.processRawPacket(target.value);
      }
    });
    await channel.startNotifications();

    self.gattServer = gattServer;
    self.channel = channel;

    try {
      await self.initialNegotiate();
      await this.fetchPrinterInfo();
    } catch (e) {
      console.error('[Niimbot] Unable to fetch printer info', e);
    }

    return {
      deviceName: device.name,
      result: self.info?.connectResult ?? 1,
    };
  }

  private async findSuitableCharacteristic(
    server: BluetoothRemoteGATTServer
  ): Promise<BluetoothRemoteGATTCharacteristic | undefined> {
    const services = await server.getPrimaryServices();
    for (const service of services) {
      if (service.uuid.length < 5) continue;
      const characteristics = await service.getCharacteristics();
      for (const c of characteristics) {
        if (c.properties.notify && c.properties.writeWithoutResponse) {
          return c;
        }
      }
    }
    return undefined;
  }
}

export type NiimbotConnectMode = 'reused' | 'silent-reconnect' | 'chooser';

export type NiimbotConnectResult = {
  client: ReconnectableNiimbotBleClient;
  mode: NiimbotConnectMode;
  deviceName?: string;
};

let sharedClient: ReconnectableNiimbotBleClient | null = null;

/**
 * Returns a connected Niimbot client. Strategy (in order):
 *   1. Reuse the existing connected singleton if any.
 *   2. Try silent reconnect via `navigator.bluetooth.getDevices()`.
 *   3. Fall back to `requestDevice()` chooser.
 *
 * Must be called from a user-gesture handler (button click) — Web Bluetooth
 * requires it for the chooser path.
 */
export async function getNiimbotClient(): Promise<NiimbotConnectResult> {
  if (sharedClient?.isConnected()) {
    return { client: sharedClient, mode: 'reused' };
  }

  const client = sharedClient ?? new ReconnectableNiimbotBleClient();
  sharedClient = client;

  // 2. Silent reconnect via permitted-devices API (Chrome 85+)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bt = (navigator as any).bluetooth;
  if (bt && typeof bt.getDevices === 'function') {
    try {
      const devices: BluetoothDevice[] = await bt.getDevices();
      // Niimbot device names start with the printer model letter (B/D/T...)
      const known = devices.find((d) => d.name && /^[BDTPMAS]/.test(d.name));
      if (known) {
        try {
          const info = await client.connectToDevice(known);
          return {
            client,
            mode: 'silent-reconnect',
            deviceName: info.deviceName,
          };
        } catch (err) {
          console.info(
            '[Niimbot] silent reconnect failed, falling back to chooser',
            err
          );
        }
      }
    } catch (err) {
      console.info('[Niimbot] getDevices() not available', err);
    }
  }

  // 3. Chooser
  const info = await client.connect();
  return { client, mode: 'chooser', deviceName: info.deviceName };
}

export async function disconnectNiimbotClient(): Promise<void> {
  if (sharedClient) {
    try {
      await sharedClient.disconnect();
    } catch {
      /* ignore */
    }
  }
}

export function getCurrentNiimbotClient(): ReconnectableNiimbotBleClient | null {
  return sharedClient?.isConnected() ? sharedClient : null;
}
